import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type { Meeting, MeetingsFilters, CreateMeetingPayload, UpdateMeetingPayload } from '../types';

interface MeetingsResponse {
  meetings: Meeting[];
}

// ─── Queries ────────────────────────────────────────────────────────────────

export function useMeetings(filters: MeetingsFilters = {}) {
  return useQuery({
    queryKey: ['meetings', 'list', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      const qs = params.toString();
      const { data } = await api.get<MeetingsResponse>(`/api/meetings${qs ? `?${qs}` : ''}`);
      return data.meetings ?? [];
    },
    placeholderData: keepPreviousData,
  });
}

/** Next upcoming meetings for the current user (backed by GET /api/meetings/upcoming). */
export function useUpcomingMeetings() {
  return useQuery({
    queryKey: ['meetings', 'upcoming'],
    queryFn: async () => {
      const { data } = await api.get<MeetingsResponse>('/api/meetings/upcoming');
      return data.meetings ?? [];
    },
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function useMeetingMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['meetings'] });

  const createMeeting = useMutation({
    mutationFn: async (payload: CreateMeetingPayload) => {
      const { data } = await api.post<{ ok: boolean; id: string }>('/api/meetings', payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const updateMeeting = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateMeetingPayload }) => {
      const { data } = await api.patch<{ ok: boolean }>(`/api/meetings/${id}`, payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const deleteMeeting = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete<{ ok: boolean }>(`/api/meetings/${id}`);
      return data;
    },
    onSuccess: invalidate,
  });

  return { createMeeting, updateMeeting, deleteMeeting };
}
